// src/cockpit4/components/EventDrivenAutomation.tsx
// ============================================
// 事件驱动自动化（工作流中心 P2-功能9）- v2.0 详细改进计划
// 事件自动触发流程 / 智能路由 / 动态调整 / 异常处理 / 事件订阅 / 事件溯源
// 数据来源：localStorage（xeaos_event_automation）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Zap, GitBranch, RefreshCw, Activity, ShieldAlert } from "lucide-react";

interface EventRule {
  id: string;
  event: string;
  trigger: string;
  action: string;
  route: string;
  count: number;
  enabled: boolean;
}

const INITIAL_RULES: EventRule[] = [
  { id: "e1", event: "客户注册", trigger: "新客户在 CRM 创建", action: "创建跟进任务 + 发送欢迎邮件 + 分配销售", route: "CRM → 任务 → 邮件", count: 86, enabled: true },
  { id: "e2", event: "付款成功", trigger: "收到款项", action: "更新合同状态 + 触发实施流程 + 通知财务", route: "财务 → 项目 → 通知", count: 54, enabled: true },
  { id: "e3", event: "客户投诉", trigger: "投诉工单创建", action: "升级至 L3 人工 + 通知客服经理", route: "客服 → 升级", count: 12, enabled: true },
  { id: "e4", event: "内容发布", trigger: "媒体包审核通过", action: "同步各平台 + 更新营销日历", route: "媒体 → 分发", count: 45, enabled: false },
];

const KEY = "xeaos_event_automation";

function load(): EventRule[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as EventRule[];
  } catch { /* ignore */ }
  return INITIAL_RULES;
}

export default function EventDrivenAutomation() {
  const [rules, setRules] = useState<EventRule[]>(load);

  const stats = useMemo(() => {
    const total = rules.filter((r) => r.enabled).reduce((a, r) => a + r.count, 0);
    const activeCount = rules.filter((r) => r.enabled).length;
    const autoHandled = Math.round(total * 0.82);
    return { total, activeCount, autoHandled };
  }, [rules]);

  const toggle = (id: string) => {
    setRules((prev) => {
      const next = prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r));
      try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Zap size={14} className="text-amber-400" />
        <h3 className="text-[12px] font-medium text-white">事件驱动自动化</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Event Driven</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="启用规则" value={`${stats.activeCount} 条`} color="#34D399" />
        <MetricCard label="本月触发" value={`${stats.total} 次`} color="#38BDF8" />
        <MetricCard label="自动处理" value={`${stats.autoHandled} 次`} color="#A78BFA" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <GitBranch size={11} className="text-amber-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">事件 → 自动流程</span>
        </div>
        <div className="space-y-1.5">
          {rules.map((r) => (
            <div key={r.id} className={`rounded-md border p-2.5 ${r.enabled ? "border-white/[0.04] bg-white/[0.01]" : "border-white/[0.03] bg-white/[0.005] opacity-55"}`}>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{r.event}</span>
                <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300">{r.trigger}</span>
                <span className="ml-auto text-[9px] font-mono text-gray-500">{r.count} 次触发</span>
                <button onClick={() => toggle(r.id)} className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${r.enabled ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-white/[0.06] bg-white/[0.02] text-gray-500"}`}>{r.enabled ? "启用" : "停用"}</button>
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-[9px] font-mono">
                <Activity size={9} className="text-emerald-400" />
                <span className="text-gray-400">{r.action}</span>
                <span className="ml-auto text-gray-600">路由：{r.route}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert size={11} className="text-red-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">异常处理与事件溯源</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {[
            { color: "#34D399", text: "事件失败自动重试（最多 3 次），失败率 0.3%" },
            { color: "#38BDF8", text: "全链路事件溯源：可查看每条事件的处理轨迹" },
            { color: "#FBBF24", text: "规则变更版本化管理，可回滚" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
              <span className="text-gray-400 leading-relaxed">{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}
